import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  ImageRun,
  Packer,
  PageNumber,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from "docx";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import "pdfjs-dist/legacy/build/pdf.worker.entry";

/* ------------------------------------------------------------------ */
/* Layout – A4, black & white only                                     */
/* ------------------------------------------------------------------ */
const FONT = "Nirmala UI";
const PAGE_W = 11906;
const PAGE_H = 16838;
const MARGIN = {
  top: 851,
  bottom: 851,
  left: 851,
  right: 567,
  header: 284,
  footer: 340,
};
const CONTENT_W = PAGE_W - MARGIN.left - MARGIN.right; // 10488

const SZ = { body: 20, head: 22, title: 26, small: 18 };

const LINE = { style: BorderStyle.SINGLE, size: 4, color: "000000" };
const TABLE_BORDERS = {
  top: LINE,
  bottom: LINE,
  left: LINE,
  right: LINE,
  insideHorizontal: LINE,
  insideVertical: LINE,
};
const NO_LINE = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
const NO_BORDERS = {
  top: NO_LINE,
  bottom: NO_LINE,
  left: NO_LINE,
  right: NO_LINE,
  insideHorizontal: NO_LINE,
  insideVertical: NO_LINE,
};
const CELL_MARGINS = { top: 50, bottom: 50, left: 100, right: 100 };

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const isValidDataUrl = (url) =>
  typeof url === "string" && url.startsWith("data:") && url.includes("base64,");

function dataUrlToUint8(dataUrl) {
  if (!isValidDataUrl(dataUrl)) return { bytes: null, mime: "" };
  try {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return { bytes: u8arr, mime };
  } catch {
    return { bytes: null, mime: "" };
  }
}

function imageTypeFromMime(mime) {
  if (/png/i.test(mime)) return "png";
  if (/gif/i.test(mime)) return "gif";
  if (/bmp/i.test(mime)) return "bmp";
  return "jpg";
}

function safeImageRun(dataUrl, width, height) {
  const { bytes, mime } = dataUrlToUint8(dataUrl);
  if (!bytes || !bytes.length) return null;
  return new ImageRun({
    type: imageTypeFromMime(mime),
    data: bytes,
    transformation: { width, height },
  });
}

async function fetchAsDataURL(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function pdfToImages(pdfUrl, scale = 1.5) {
  const pdf = await pdfjsLib.getDocument({ url: pdfUrl }).promise;
  const out = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: ctx, viewport }).promise;
    out.push(canvas.toDataURL("image/png"));
  }
  return out;
}

const pickText = (ai, manual) =>
  ai && String(ai).trim() !== "" ? ai : manual || "";

const getObservation = (item) => pickText(item.aiObservation, item.observation);
const getSuggestion = (item) => pickText(item.aiSuggestion, item.suggestion);

function para(
  text,
  {
    bold = false,
    size = SZ.body,
    align = AlignmentType.LEFT,
    italic = false,
    after = 60,
    before = 0,
  } = {},
) {
  return new Paragraph({
    alignment: align,
    spacing: { after, before },
    children: [
      new TextRun({
        text: text ?? "",
        bold,
        size,
        font: FONT,
        italics: italic,
        color: "000000",
      }),
    ],
  });
}

function bodyPara(text, { after = 80, before = 0, bold = false } = {}) {
  return new Paragraph({
    alignment: AlignmentType.BOTH,
    spacing: { after, before },
    children: [
      new TextRun({
        text: text ?? "",
        bold,
        size: SZ.body,
        font: FONT,
        color: "000000",
      }),
    ],
  });
}

function sectionHeading(text) {
  return new Paragraph({
    alignment: AlignmentType.LEFT,
    spacing: { before: 240, after: 120 },
    keepNext: true,
    children: [
      new TextRun({
        text,
        bold: true,
        size: SZ.head,
        font: FONT,
        color: "000000",
      }),
    ],
  });
}

function title(text) {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { before: 200, after: 200 },
    children: [
      new TextRun({
        text,
        bold: true,
        size: SZ.title,
        font: FONT,
        color: "000000",
      }),
    ],
  });
}

function centered(text, size = SZ.body, bold = true) {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 40 },
    children: [
      new TextRun({
        text,
        size,
        bold,
        font: FONT,
        color: "000000",
      }),
    ],
  });
}

function cell(children, width, opts = {}) {
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    margins: CELL_MARGINS,
    verticalAlign: opts.valign || VerticalAlign.TOP,
    columnSpan: opts.columnSpan,
    children: children.length ? children : [para("")],
  });
}

function plainTable(columnWidths, rows) {
  return new Table({
    width: {
      size: columnWidths.reduce((a, b) => a + b, 0),
      type: WidthType.DXA,
    },
    columnWidths,
    borders: TABLE_BORDERS,
    rows,
  });
}

function headerRow(labels, widths) {
  return new TableRow({
    tableHeader: true,
    cantSplit: true,
    children: labels.map((t, i) =>
      cell(
        [para(t, { bold: true, align: AlignmentType.CENTER, after: 0 })],
        widths[i],
        { valign: VerticalAlign.CENTER },
      ),
    ),
  });
}

function imagesInRow(imageUrls, imgWidth = 280, imgHeight = 200, perRow = 3) {
  const colW = Math.floor(CONTENT_W / perRow);
  const columnWidths = Array(perRow).fill(colW);
  const rows = [];
  for (let i = 0; i < imageUrls.length; i += perRow) {
    const slice = imageUrls.slice(i, i + perRow);
    const cells = [];
    for (let c = 0; c < perRow; c++) {
      const run = slice[c] ? safeImageRun(slice[c], imgWidth, imgHeight) : null;
      cells.push(
        new TableCell({
          width: { size: colW, type: WidthType.DXA },
          margins: CELL_MARGINS,
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: run ? [run] : [],
            }),
          ],
        }),
      );
    }
    rows.push(new TableRow({ cantSplit: true, children: cells }));
  }
  return new Table({
    width: { size: colW * perRow, type: WidthType.DXA },
    columnWidths,
    borders: NO_BORDERS,
    rows,
  });
}
function buildObservationBlock(counter, item, isQuestion) {
  const blocks = [];
  const obsText = getObservation(item);
  const sugText = getSuggestion(item);

  // Question mode: question becomes the heading.
  // Non-question mode: observation becomes the heading.
  const headingText =
    isQuestion && item.question
      ? `${counter}) ${item.question}`
      : `${counter}) ${obsText}`;

  blocks.push(
    new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { before: 180, after: 80 },
      keepNext: true,
      children: [
        new TextRun({
          text: headingText,
          bold: true,
          size: SZ.head,
          font: FONT,
          color: "000000",
        }),
      ],
    }),
  );

  // Display the observation answer only when question mode is enabled.
  if (isQuestion && obsText.trim() !== "") {
    blocks.push(bodyPara(obsText.trim(), { after: 80 }));
  }

  // Display suggestion directly after the heading or observation answer.
  if (sugText.trim() !== "") {
    blocks.push(bodyPara(sugText.trim(), { after: 80 }));
  }

  // Keep observation images unchanged.
  const validImages = (item.image || []).filter(isValidDataUrl);

  if (validImages.length > 0) {
    const imageParas = validImages
      .map((url) => safeImageRun(url, 180, 135))
      .filter(Boolean)
      .map(
        (run) =>
          new Paragraph({
            alignment: AlignmentType.LEFT,
            spacing: { before: 60, after: 80 },
            children: [run],
          }),
      );

    blocks.push(...imageParas);
  }

  return blocks;
}
/* ------------------------------------------------------------------ */
/* Main builder                                                        */
/* ------------------------------------------------------------------ */
async function buildInspectionDocx({
  isQuestion = false,
  orgdetails,
  auddetails,
  details,
  annextureList,
  emptypeOptions,
  observationdate,
  user,
  empData,
  scheduleid,
  fetchImagesAsBase64FromServer,
  fetchRespImagesAsBase64FromServer,
  fetchEmployeeImage,
  fetchOrgImage,
  lastNote,
  introductionContent,
}) {
  const industry = JSON.parse(auddetails.industry || "{}");
  const occupiers = JSON.parse(auddetails.occupier || "[]");
  const employees = JSON.parse(auddetails.employee || "[]");
  const occ = occupiers[0] || {};
  const employeeTypeMap = Object.fromEntries(
    (emptypeOptions || []).map((o) => [o.value, o.label]),
  );
  const totalMale = employees.reduce((s, e) => s + Number(e.adultMale || 0), 0);
  const totalFemale = employees.reduce(
    (s, e) => s + Number(e.adultFemale || 0),
    0,
  );
  const officer = empData.find((d) => d.code === user) || {};
  const inspectionOfficerName = officer.name || user;
  const inspectionOfficerDesignation = officer.designation || "";

  let logoDataUrl = null;
  if (orgdetails?.logo) {
    if (isValidDataUrl(orgdetails.logo)) {
      logoDataUrl = orgdetails.logo;
    } else {
      try {
        const logoName = orgdetails.logo.split("/").pop();
        const logoBlobUrl = await fetchOrgImage(logoName, orgdetails.code);
        const fetched = await fetchAsDataURL(logoBlobUrl);
        if (isValidDataUrl(fetched)) logoDataUrl = fetched;
      } catch {
        // omit logo
      }
    }
  }

  /* Org header lines – only if present */
  const orgName = (orgdetails?.name || "").trim();
  const orgAddress = [
    orgdetails?.addressline1,
    orgdetails?.city,
    orgdetails?.dist,
    orgdetails?.state,
    orgdetails?.country,
    orgdetails?.pin,
  ]
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(", ");
  const orgContact = [orgdetails?.contact, orgdetails?.emailid]
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(" | ");

  const enriched = await Promise.all(
    (details || []).map(async (item) => {
      const [obsImgs, respImgs] = await Promise.all([
        fetchImagesAsBase64FromServer((item.image || []).join(","), scheduleid),
        fetchRespImagesAsBase64FromServer(
          (item.responseImages || []).join(","),
          scheduleid,
          item.department,
        ),
      ]);

      const obsBase64 = await Promise.all(
        (obsImgs || []).map(async (url) =>
          isValidDataUrl(url)
            ? url
            : await fetchAsDataURL(url).catch(() => null),
        ),
      );
      const respBase64 = await Promise.all(
        (respImgs || []).map(async (url) =>
          isValidDataUrl(url)
            ? url
            : await fetchAsDataURL(url).catch(() => null),
        ),
      );

      return {
        ...item,
        image: obsBase64.filter(isValidDataUrl),
        responseImages: respBase64.filter(isValidDataUrl),
      };
    }),
  );

  const annexImages = [];
  for (const a of annextureList || []) {
    if (!a?.path) continue;
    const filename = a.path.split("/").pop().toLowerCase();
    try {
      const blobUrl = await fetchEmployeeImage(filename, a.value);
      if (filename.endsWith(".pdf")) {
        const pages = await pdfToImages(blobUrl, 1.5);
        pages
          .filter(isValidDataUrl)
          .forEach((p) => annexImages.push({ label: a.label, dataUrl: p }));
      } else {
        const img = await fetchAsDataURL(blobUrl);
        if (isValidDataUrl(img))
          annexImages.push({ label: a.label, dataUrl: img });
      }
    } catch {}
  }

  const grouped = enriched.reduce((acc, it) => {
    (acc[it.chapter] ||= []).push(it);
    return acc;
  }, {});

  const body = [];

  /* Letter-head: logo + org details only */
  if (logoDataUrl) {
    const logoRun = safeImageRun(logoDataUrl, 70, 55);
    if (logoRun) {
      body.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 60 },
          children: [logoRun],
        }),
      );
    }
  }

  if (orgName) {
    body.push(centered(orgName, SZ.title, true));
  }
  if (orgAddress) {
    body.push(centered(orgAddress, SZ.body, false));
  }
  if (orgContact) {
    body.push(centered(orgContact, SZ.small, false));
  }

  body.push(
    new Paragraph({
      spacing: { before: 80, after: 120 },
      border: {
        bottom: {
          style: BorderStyle.SINGLE,
          size: 12,
          color: "000000",
          space: 1,
        },
      },
      children: [],
    }),
  );

  body.push(title("Inspection Report"));

  /* 1) Factory Details */
  const factoryW = [3600, CONTENT_W - 3600];
  const factoryRows = [
    ["Inspection Officer Name", inspectionOfficerName],
    ["Inspection Officer Designation", inspectionOfficerDesignation],
    ["Factory Name", auddetails.name],
    [
      "Factory Address",
      [auddetails.city, auddetails.dist, auddetails.state]
        .filter(Boolean)
        .join(", "),
    ],
    ["License No", auddetails.licencenumber],
    ["Date of Inspection", observationdate],
    ["Occupier Name", industry.occupieName],
    [
      "Occupier Contact & Email",
      [industry.occupierPhone, occ.occupierEmail].filter(Boolean).join(" | "),
    ],
    ["Manager Name", industry.managerName],
    [
      "Manager Contact & Email",
      [industry.managerPhone, industry.managerEmail]
        .filter(Boolean)
        .join(" | "),
    ],
    [
      "Management Personnel present at the time of inspection",
      industry.managementper,
    ],
  ];

  body.push(sectionHeading("1) Factory Details"));
  body.push(
    plainTable(
      factoryW,
      factoryRows.map(
        ([label, value]) =>
          new TableRow({
            cantSplit: true,
            children: [
              cell([para(label, { after: 0 })], factoryW[0]),
              cell([para(value || "", { after: 0 })], factoryW[1]),
            ],
          }),
      ),
    ),
  );

  /* 2) Employees Summary */
  const empW = [CONTENT_W - 2 * 2400, 2400, 2400];
  body.push(sectionHeading("2) Employees Summary"));
  body.push(
    plainTable(empW, [
      headerRow(["Employee Type", "Adult Male", "Adult Female"], empW),
      ...employees.map(
        (e) =>
          new TableRow({
            cantSplit: true,
            children: [
              cell(
                [
                  para(
                    employeeTypeMap[e.employeeType] || `Type ${e.employeeType}`,
                    { after: 0 },
                  ),
                ],
                empW[0],
              ),
              cell(
                [
                  para(String(e.adultMale || 0), {
                    align: AlignmentType.CENTER,
                    after: 0,
                  }),
                ],
                empW[1],
              ),
              cell(
                [
                  para(String(e.adultFemale || 0), {
                    align: AlignmentType.CENTER,
                    after: 0,
                  }),
                ],
                empW[2],
              ),
            ],
          }),
      ),
      new TableRow({
        cantSplit: true,
        children: [
          cell([para("Total", { bold: true, after: 0 })], empW[0]),
          cell(
            [
              para(String(totalMale), {
                bold: true,
                align: AlignmentType.CENTER,
                after: 0,
              }),
            ],
            empW[1],
          ),
          cell(
            [
              para(String(totalFemale), {
                bold: true,
                align: AlignmentType.CENTER,
                after: 0,
              }),
            ],
            empW[2],
          ),
        ],
      }),
    ]),
  );

  const introduction = String(introductionContent || "").trim();
  const closingNote = String(lastNote || "").trim();

  // 3) Opening Notes
  if (introduction) {
    body.push(sectionHeading("3) Opening Notes"));
    body.push(bodyPara(introduction));
  }

  /* 3) Observations */
  body.push(
    sectionHeading(
      "4) Inspection Observations and Suggested Corrective Actions",
    ),
  );

  let counter = 1;
  let hasAnyObservation = false;
  for (const items of Object.values(grouped)) {
    for (const item of items) {
      const hasObs = getObservation(item).trim() !== "";
      const hasSug = getSuggestion(item).trim() !== "";
      if (!hasObs && !hasSug) continue;

      hasAnyObservation = true;
      body.push(...buildObservationBlock(counter, item, isQuestion));
      counter++;
    }
  }

  if (!hasAnyObservation) {
    body.push(para("No observations recorded.", { italic: true, after: 120 }));
  }

  // 5) Closing Notes - after observations
  if (closingNote) {
    body.push(sectionHeading("5) Closing Notes"));
    body.push(bodyPara(closingNote));
  }

  /* Place / Date / Signature */
  const signW = [CONTENT_W / 2, CONTENT_W / 2];
  const signLine = (text) => para(text, { after: 140 });

  body.push(new Paragraph({ spacing: { before: 280 }, children: [] }));
  body.push(
    new Table({
      width: { size: CONTENT_W, type: WidthType.DXA },
      columnWidths: signW,
      borders: NO_BORDERS,
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            cell(
              [
                signLine("Place: ________________________"),
                signLine("Date of issue: ________________"),
              ],
              signW[0],
            ),
            cell(
              [
                signLine("Signature: ____________________________"),
                signLine("Name: ________________________________"),
              ],
              signW[1],
            ),
          ],
        }),
      ],
    }),
  );

  // Separate designation table below the signature table
  body.push(
    new Table({
      width: { size: CONTENT_W, type: WidthType.DXA },
      columnWidths: signW,
      borders: NO_BORDERS,
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            cell(
              [
                para("Additional Director of Factories and Boilers", {
                  bold: true,
                  size: SZ.small,
                  align: AlignmentType.LEFT,
                  after: 0,
                }),
              ],
              signW[1],
            ),
            cell([], signW[0]),
          ],
        }),
      ],
    }),
  );

  /* Annexures */
  if (annexImages.length) {
    body.push(new Paragraph({ pageBreakBefore: true, children: [] }));
    body.push(title("Annexures"));
    const groupedByLabel = annexImages.reduce((acc, img) => {
      (acc[img.label] ||= []).push(img.dataUrl);
      return acc;
    }, {});
    for (const [label, imgs] of Object.entries(groupedByLabel)) {
      body.push(sectionHeading(label || "Annexure"));
      body.push(imagesInRow(imgs, 240, 320, 2));
    }
  }

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: FONT, size: SZ.body, color: "000000" },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            size: { width: PAGE_W, height: PAGE_H },
            margin: MARGIN,
          },
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: SZ.body,
                    font: FONT,
                    color: "000000",
                  }),
                ],
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [],
              }),
            ],
          }),
        },
        children: body,
      },
    ],
  });

  return await Packer.toBlob(doc);
}

export default buildInspectionDocx;
